/**
 * La page publique d'un entrepreneur : eventmatch.pro/<pseudo>.
 *
 * Type Calendly — on choisit un créneau, on laisse son nom — mais avec ce que
 * Calendly n'a pas : l'influence de la personne, son entreprise, ses services.
 * Le visiteur sans compte en reçoit un en réservant, parrainé par l'hôte ; et
 * /<pseudo>/join saute directement à l'inscription.
 *
 * Trilingue via `t()` : c'est la page la plus partagée hors de l'application,
 * elle se lit dans la langue du téléphone qui l'ouvre.
 */
import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ArrowUpRight, Calendar, Check, MapPin } from 'lucide-react';
import { appelerApi, envoyer } from '../reseau.ts';
import { useLibelles } from '../i18n.tsx';
import { Marque } from '../composants/Marque.tsx';
import { Etoile } from '../composants/Etoile.tsx';
import { lienSuivi, nomDe, reseauDe, type PagePublique } from './compte/types.ts';

export function PagePerso({ pseudo, join }: { pseudo: string; join: boolean }) {
  const { t, langue } = useLibelles();
  const [page, setPage] = useState<PagePublique | null | undefined>(undefined);

  useEffect(() => {
    void appelerApi<PagePublique>(`/api/agenda/p/${encodeURIComponent(pseudo)}`).then((r) => setPage(r.donnees ?? null));
  }, [pseudo]);

  // /<pseudo>/join : droit à l'inscription, parrainée.
  useEffect(() => {
    if (join && page) location.replace(`/compte/creer?p=${page.code_parrainage}`);
  }, [join, page]);

  if (page === undefined) return <p className="min-h-screen bg-void p-10 text-fumee">{t('app.chargement', 'Chargement…')}</p>;
  if (page === null) return <Navigate to="/" replace />;
  if (join) return null;

  const p = page.profil;
  const nom = nomDe(p);

  return (
    <div className="min-h-screen bg-void font-sans text-blanc">
      <header className="mx-auto flex max-w-[1080px] items-center justify-between px-5 py-5">
        <a href="/" className="flex items-center gap-2.5">
          <Marque taille={20} />
          <span className="text-[15px] font-medium">EventMatch<span className="text-fumee">.pro</span></span>
        </a>
        <a href={page.c_est_moi ? '/compte/rendez-vous' : page.inscription_url} className="text-[13px] text-fumee hover:text-blanc">
          {page.c_est_moi ? t('page_perso.mon_espace', 'Mon espace') : t('page_perso.creer_ma_page', 'Créer ma page EventMatch')}
        </a>
      </header>

      <main className="mx-auto grid max-w-[1080px] gap-6 px-5 pb-24 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        {/* La personne et l'entreprise */}
        <section>
          <div className="flex items-start gap-4">
            {p.photo_url || p.logo_url ? (
              <img src={p.photo_url ?? p.logo_url ?? ''} alt="" className="h-20 w-20 rounded-full object-cover" />
            ) : (
              <span className="flex h-20 w-20 items-center justify-center rounded-full border border-graphite font-serif text-[28px]">
                {nom.split(/\s+/).map((m) => m[0]).slice(0, 2).join('').toUpperCase()}
              </span>
            )}
            <div className="min-w-0">
              <h1 className="flex flex-wrap items-center gap-3 text-[28px] font-light leading-tight">{nom} <Etoile premium={p.premium} /></h1>
              <p className="mt-1 text-[14px] text-fumee">{[p.fonction, p.entreprise].filter(Boolean).join(' · ')}</p>
              {p.secteur && (
                <p className="mt-1 flex items-center gap-1.5 text-[13px] text-fumee">
                  <MapPin size={12} strokeWidth={1.5} /> {p.secteur}
                </p>
              )}
            </div>
          </div>

          {page.influence && (
            <div className="mt-6 flex flex-wrap items-end gap-6 rounded-[12px] border border-graphite p-4">
              <div>
                <p className="label-caps text-fumee">{t('page_perso.influence', 'Score d’influence')}</p>
                <p className="mt-1 flex items-baseline gap-2">
                  <span className="text-match-gradient font-serif text-[40px] leading-none">{page.influence.global}</span>
                  <span className="text-[13px] text-fumee">/ 1000</span>
                </p>
                <p className="mt-1 text-[13px]">{page.influence.palier}</p>
              </div>
              <div className="flex gap-5 text-[12px] text-fumee">
                <span>{t('page_perso.sociale', 'Sociale')} <span className="text-blanc">{page.influence.sociale}</span></span>
                <span>{t('page_perso.reseau', 'Réseau')} <span className="text-blanc">{page.influence.reseau}</span></span>
                <span>{t('page_perso.virtuelle', 'Virtuelle')} <span className="text-blanc">{page.influence.virtuelle}</span></span>
              </div>
            </div>
          )}

          {(p.pitch || p.bio) && <p className="mt-6 max-w-[560px] text-[15px] leading-relaxed text-blanc/90">{p.pitch ?? p.bio}</p>}

          {(p.offres.length > 0 || p.services) && (
            <div className="mt-6">
              <p className="label-caps text-fumee">{t('page_perso.services', 'Ce que propose l’entreprise')}</p>
              {p.offres.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {p.offres.map((o) => (
                    <span key={o.nom} title={o.detail ?? undefined} className="rounded-full border border-graphite px-3 py-1.5 text-[13px]">
                      {o.nom}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-[14px] leading-relaxed text-fumee">{p.services}</p>
              )}
            </div>
          )}

          {p.client_ideal && (
            <div className="mt-5">
              <p className="label-caps text-fumee">{t('page_perso.client_ideal', 'Pour qui')}</p>
              <p className="mt-2 text-[14px] leading-relaxed text-fumee">{p.client_ideal}</p>
            </div>
          )}

          {(p.site_web || p.linkedin || p.liens.length > 0) && (
            <div className="mt-6 flex flex-wrap gap-3 text-[13px]">
              {p.site_web && <a href={lienSuivi(p.personne_id, p.site_web)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">{t('page_perso.site', 'Site web')} <ArrowUpRight size={12} strokeWidth={1.5} /></a>}
              {p.linkedin && <a href={lienSuivi(p.personne_id, p.linkedin)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">LinkedIn <ArrowUpRight size={12} strokeWidth={1.5} /></a>}
              {p.liens.map((l) => (
                <a key={l.url} href={lienSuivi(p.personne_id, l.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                  {l.libelle || reseauDe(l.url)} <ArrowUpRight size={12} strokeWidth={1.5} />
                </a>
              ))}
            </div>
          )}
        </section>

        {/* La prise de rendez-vous */}
        <section className="rounded-[14px] border border-graphite bg-charbon p-5">
          <p className="label-caps text-fumee">{t('page_perso.prendre_rdv', 'Prendre rendez-vous')}</p>
          <Reservation page={page} pseudo={pseudo} langue={langue} t={t} />
        </section>
      </main>

      <footer className="mx-auto max-w-[1080px] px-5 pb-10 text-[12px] text-fumee">
        {t('page_perso.pied', 'EventMatch.pro — le réseau des entrepreneurs qui se rencontrent utilement. Offert par Elite Lead.')}
      </footer>
    </div>
  );
}

function Reservation({ page, pseudo, langue, t }: { page: PagePublique; pseudo: string; langue: string; t: (c: string, d?: string) => string }) {
  const [choisi, setChoisi] = useState<string | null>(null);
  const [f, setF] = useState({ prenom: '', nom: '', email: '', entreprise: '', message: '' });
  const [etat, setEtat] = useState<'repos' | 'envoi' | 'ok'>('repos');
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirme, setConfirme] = useState<{ debut: string; nouveauCompte: boolean; connecte: boolean } | null>(null);

  const locale = langue === 'nl' ? 'nl-BE' : langue === 'en' ? 'en-GB' : 'fr-BE';
  const jour = (iso: string) => new Date(iso).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Brussels' });
  const heure = (iso: string) => new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels' });

  const parJour = useMemo(() => {
    const m = new Map<string, PagePublique['creneaux']>();
    for (const c of page.creneaux) {
      const k = new Date(c.debut).toLocaleDateString('en-CA', { timeZone: 'Europe/Brussels' });
      m.set(k, [...(m.get(k) ?? []), c]);
    }
    return [...m.entries()];
  }, [page.creneaux]);

  const prenomHote = page.profil.prenom ?? nomDe(page.profil);

  if (confirme) {
    return (
      <div className="mt-4">
        <p className="flex items-center gap-2 text-[16px] text-blanc"><Check size={16} strokeWidth={1.5} /> {t('page_perso.confirme', 'Rendez-vous confirmé')}</p>
        <p className="mt-2 text-[14px] text-fumee">{jour(confirme.debut)} · {heure(confirme.debut)}</p>
        <p className="mt-3 text-[13px] leading-relaxed text-fumee">
          {confirme.nouveauCompte
            ? t('page_perso.compte_cree', 'Votre espace EventMatch est prêt : vous y retrouvez ce rendez-vous, et les entrepreneurs qui vous correspondent.')
            : confirme.connecte
              ? t('page_perso.rdv_dans_espace', 'Le rendez-vous est dans votre espace EventMatch.')
              : t('page_perso.deja_compte', 'Vous avez déjà un compte EventMatch : connectez-vous pour retrouver ce rendez-vous dans votre espace.')}
        </p>
        <a href={confirme.connecte || confirme.nouveauCompte ? '/compte/rendez-vous' : '/compte'} className="btn-creme mt-4 inline-block !min-h-0 px-5 py-2 text-[14px]">
          {confirme.connecte || confirme.nouveauCompte ? t('page_perso.ouvrir_espace', 'Ouvrir mon espace') : t('page_perso.se_connecter', 'Me connecter')}
        </a>
      </div>
    );
  }

  if (page.quota.complet) {
    return (
      <p className="mt-4 max-w-[480px] text-[14px] leading-relaxed text-fumee">
        {t('page_perso.complet', '{prenom} a déjà booké ses {n} rendez-vous au calendrier, vous pouvez lui recommander de passer Premium pour débloquer plus de créneaux.')
          .replace('{prenom}', prenomHote)
          .replace('{n}', String(page.quota.limite ?? 5))}
      </p>
    );
  }

  if (!page.disponibilites_definies || page.creneaux.length === 0) {
    return (
      <p className="mt-4 text-[14px] leading-relaxed text-fumee">
        {t('page_perso.pas_de_creneau', '{prenom} n’a pas encore ouvert de créneaux. Repassez bientôt — ou inscrivez-vous pour être prévenu.').replace('{prenom}', prenomHote)}
      </p>
    );
  }

  const reserver = async () => {
    if (!choisi) return;
    setEtat('envoi');
    setErreur(null);
    const r = await envoyer<{ debut: string; nouveauCompte: boolean; connecte: boolean }>(`/api/agenda/p/${encodeURIComponent(pseudo)}/reserver`, {
      debut: choisi,
      email: f.email.trim() || null,
      prenom: f.prenom.trim() || null,
      nom: f.nom.trim() || null,
      entreprise: f.entreprise.trim() || null,
      message: f.message.trim() || null,
    });
    setEtat('repos');
    if (r.erreur || !r.donnees) {
      setErreur(r.erreur ?? t('page_perso.erreur', 'La réservation a échoué'));
      return;
    }
    setConfirme(r.donnees);
  };

  return (
    <div className="mt-3">
      <div className="max-h-[420px] overflow-y-auto pr-1">
        {parJour.map(([k, creneaux]) => (
          <div key={k} className="mb-4">
            <p className="flex items-center gap-1.5 text-[13px] capitalize text-blanc"><Calendar size={12} strokeWidth={1.5} /> {jour(creneaux[0]!.debut)}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {creneaux.map((c) => (
                <button
                  key={c.debut}
                  onClick={() => setChoisi(c.debut)}
                  className={`rounded-full border border-graphite px-3 py-1.5 text-[13px] transition-colors ${choisi === c.debut ? 'bg-blanc text-void' : 'text-fumee hover:text-blanc'}`}
                >
                  {heure(c.debut)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {choisi && (
        <div className="mt-4 border-t border-graphite pt-4">
          <p className="text-[14px] text-blanc">{jour(choisi)} · {heure(choisi)}</p>
          {!page.c_est_moi && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input value={f.prenom} onChange={(e) => setF({ ...f, prenom: e.target.value })} placeholder={t('page_perso.prenom', 'Prénom')} className="field-input !min-h-[40px] !py-2 !text-sm" />
              <input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} placeholder={t('page_perso.nom', 'Nom')} className="field-input !min-h-[40px] !py-2 !text-sm" />
              <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder={t('page_perso.email', 'E-mail')} type="email" className="field-input !min-h-[40px] !py-2 !text-sm" />
              <input value={f.entreprise} onChange={(e) => setF({ ...f, entreprise: e.target.value })} placeholder={t('page_perso.entreprise', 'Entreprise')} className="field-input !min-h-[40px] !py-2 !text-sm" />
              <textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={2} placeholder={t('page_perso.message', 'De quoi voulez-vous parler ?')} className="field-input sm:col-span-2 !min-h-0 !py-2 !text-sm" />
            </div>
          )}
          {page.c_est_moi ? (
            <p className="mt-3 text-[13px] text-fumee">{t('page_perso.c_est_vous', 'C’est votre page : voilà ce que voient vos visiteurs.')}</p>
          ) : (
            <>
              <button onClick={() => void reserver()} disabled={etat === 'envoi'} className="btn-creme mt-4 !min-h-0 px-5 py-2 text-[14px] disabled:opacity-50">
                {etat === 'envoi' ? '…' : t('page_perso.confirmer', 'Confirmer le rendez-vous')}
              </button>
              <p className="mt-2 text-[12px] leading-relaxed text-fumee">
                {t('page_perso.cree_compte', 'En confirmant, vous ouvrez votre espace EventMatch (gratuit) : vous y retrouverez ce rendez-vous.')}
              </p>
            </>
          )}
          {erreur && <p className="mt-2 text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
        </div>
      )}
    </div>
  );
}
